import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

/* ---------------- GET ALL VIDEOS ---------------- */

export const fetchVideos = createAsyncThunk(
  "videos/fetchVideos",
  async ({ search = "", page = 1, limit = 10, key = "all", tag = "" }, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams({
        key,
        search,
        page: String(page),
        limit: String(limit),
      });

      if (tag) {
        queryParams.append("tag", tag);
      }

      const res = await axios.get(
        `${BASE_URL}/all/videos?${queryParams.toString()}`,
        { headers: getHeaders() }
      );

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- GET VIDEO BY ID ---------------- */

export const fetchVideoById = createAsyncThunk(
  "videos/fetchVideoById",
  async (id, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/video/${id}`, {
        headers: getHeaders(),
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- ADD VIDEO ---------------- */

export const addVideo = createAsyncThunk(
  "videos/addVideo",
  async (data, thunkAPI) => {
    try {
      const payload = {
        active: data.active,
        name: data.name_en,
        link: data.link_en,
        tag: data.tag,
        translated_videos: data.translated_videos || [],
      };

      const res = await axios.post(`${BASE_URL}/video`, payload, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- UPDATE VIDEO ---------------- */

export const updateVideo = createAsyncThunk(
  "videos/updateVideo",
  async ({ id, data }, thunkAPI) => {
    try {
      const payload = {
        video_id: id,
        tag: data.tag,
        active: data.active,
        name: data.name_en,
        link: data.link_en,
        translated_videos: data.translated_videos || [],
      };

      const res = await axios.put(`${BASE_URL}/video`, payload, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- Delete VIDEO ---------------- */
export const deleteVideo = createAsyncThunk(
  "videos/deleteVideo",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/video/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- DELETE TRANSLATED VIDEO ---------------- */

export const removeTranslatedVideo = createAsyncThunk(
  "videos/removeTranslatedVideo",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/remove-translated-video/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- Add Questions to VIDEO ---------------- */

export const addVideoQuestions = createAsyncThunk(
  "videos/addVideoQuestions",
  async (payload, thunkAPI) => {
    try {
      const res = await axios.put(`${BASE_URL}/video-questions`, payload, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- GET ACTIVE VIDEOS ---------------- */

export const fetchActiveVideos = createAsyncThunk(
  "videos/fetchActiveVideos",
  async (thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/videos`, {
        headers: getHeaders(),
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- GET EDUCATIONAL VIDEOS BY ORG ---------------- */

export const fetchEducationalVideos = createAsyncThunk(
  "videos/fetchEducationalVideos",
  async (params, thunkAPI) => {
    try {
      const orgId = typeof params === "object" ? params.orgId : params;
      const countryLanguage =
        typeof params === "object" ? params.countryLanguage : undefined;
      const res = await axios.get(`${BASE_URL}/videos/${orgId}/organization`, {
        headers: getHeaders(),
        params: countryLanguage ? { country_language: countryLanguage } : undefined,
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchVideoTags = createAsyncThunk(
  "videos/fetchVideoTags",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/all/videos-tags`, {
        headers: getHeaders(),
      });

      return Array.isArray(res.data?.data) ? res.data.data : res.data || [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- SLICE ---------------- */

const videosSlice = createSlice({
  name: "videos",

  initialState: {
    videos: [],
    activeVideos: [],
    educationalVideos: [],
    educationalCountryLanguages: [],
    videoTags: [],
    selectedVideo: null,
    page: 1,
    limit: 10,
    total: 10,
    loading: false,
    singleLoading: false,
    saving: false,
    deleting: false,
    updatingQuestions: false,
    tagsLoading: false,
    error: null,
  },

  reducers: {
    clearSelectedVideo: (state) => {
      state.selectedVideo = null;
    },
  },

  extraReducers: (builder) => {
    /* GET VIDEOS */

    builder
      .addCase(fetchVideos.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.videos = action.payload.videos;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
      })
      .addCase(fetchVideos.rejected, (state) => {
        state.loading = false;
      });

    /* GET VIDEO BY ID */

    builder
      .addCase(fetchVideoById.pending, (state) => {
        state.singleLoading = true;
      })
      .addCase(fetchVideoById.fulfilled, (state, action) => {
        state.singleLoading = false;
        state.selectedVideo = action.payload;
      })
      .addCase(fetchVideoById.rejected, (state) => {
        state.singleLoading = false;
      });

    /* ADD VIDEO */

    builder
      .addCase(addVideo.pending, (state) => {
        state.saving = true;
      })
      .addCase(addVideo.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(addVideo.rejected, (state) => {
        state.saving = false;
      });

    /* UPDATE VIDEO */

    builder
      .addCase(updateVideo.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateVideo.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateVideo.rejected, (state) => {
        state.saving = false;
      });

    /* DELETE VIDEO */

    builder
      .addCase(deleteVideo.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.deleting = false;

        state.videos = state.videos.filter(
          (video) => video.id !== action.payload
        );
      })
      .addCase(deleteVideo.rejected, (state) => {
        state.deleting = false;
      });

    builder
      .addCase(addVideoQuestions.pending, (state) => {
        state.updatingQuestions = true;
      })
      .addCase(addVideoQuestions.fulfilled, (state) => {
        state.updatingQuestions = false;
      })
      .addCase(addVideoQuestions.rejected, (state) => {
        state.updatingQuestions = false;
      });

    /* GET ACTIVE VIDEO */

    builder
      .addCase(fetchActiveVideos.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.activeVideos = action.payload;
      })
      .addCase(fetchActiveVideos.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchEducationalVideos.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEducationalVideos.fulfilled, (state, action) => {
        state.loading = false;
        state.educationalVideos = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.videos || [];
        if (Array.isArray(action.payload?.country_language_data)) {
          const countryLanguages = [
            ...state.educationalCountryLanguages,
            ...action.payload.country_language_data,
          ];

          state.educationalCountryLanguages = countryLanguages.filter(
            (item, index, options) =>
              options.findIndex((option) => `${option.id}` === `${item.id}`) ===
              index
          );
        }
      })
      .addCase(fetchEducationalVideos.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchVideoTags.pending, (state) => {
        state.tagsLoading = true;
      })
      .addCase(fetchVideoTags.fulfilled, (state, action) => {
        state.tagsLoading = false;
        state.videoTags = action.payload;
      })
      .addCase(fetchVideoTags.rejected, (state) => {
        state.tagsLoading = false;
      });
  },
});

export const { clearSelectedVideo } = videosSlice.actions;

export default videosSlice.reducer;
